







































































































































import Vue from 'vue';
import QuizStartScreen from '@/web/components/quizzes/QuizStartScreen.vue';
import QuizFinishScreen from '@/web/components/quizzes/QuizFinishScreen.vue';
import QuizUploadFile from '@/web/components/quizzes/QuizUploadFile.vue';
import QuizDynamicHeightComponent from '@/web/components/quizzes/QuizDynamicHeightComponent.vue';
import { errorToMessage } from '@/core/utils/errors';
import { quizOrQuestionnaire } from '@/core/utils/quizzes';

export default Vue.extend({
  components: {
    QuizStartScreen,
    QuizFinishScreen,
    QuizUploadFile,
    QuizDynamicHeightComponent,
  },
  props: {
    studioURL: {
      type: String,
      required: true,
    },
    quizId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentScreenType: null,
      solvedQuiz: null,
      quizInfo: null,
      currentQuestionIndex: 0,
      inProcess: false,
    };
  },
  computed: {
    currentQuestion() {
      if (this.solvedQuiz) {
        return this.solvedQuiz.quiz.quiz_questions[this.currentQuestionIndex];
      } else return null;
    },
    isLastQuestion() {
      const questionsNum = this.solvedQuiz?.quiz?.quiz_questions?.length;
      return this.currentQuestionIndex === questionsNum - 1;
    },
    canRetakeQuiz() {
      return this.quizInfo?.allow_retaking_quiz;
    },
    isGradable() {
      return this.quizInfo?.is_gradable;
    },
  },
  methods: {
    quizOrQuestionnaire,
    async start() {
      try {
        this.inProcess = true;
        const solvedQuiz = await this.$store.dispatch(
          'studentSolvedQuiz/startQuiz',
          {
            studioURL: this.studioURL,
            quizId: this.quizId,
          }
        );

        solvedQuiz.quiz.quiz_questions.forEach(question => {
          question.answers.forEach(answer => {
            answer.is_selected = false;
            answer.uploads = [];
            answer.text = '';
            answer.initial_range = answer.range;
            answer.range = null;
          });
        });

        this.solvedQuiz = solvedQuiz;

        this.setCurrentScreenType('questions');
        this.$emit('solvedQuiz', this.solvedQuiz);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    setCurrentScreenType(type) {
      this.currentScreenType = type;
      this.$emit('screenTypeChanged', type);
    },
    getSolvedQuiz(solvedQuizId: number) {
      try {
        this.$store.dispatch('studentSolvedQuiz/startQuiz', {
          studioURL: this.studioURL,
          solvedQuizId,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    answerQuizQuestion(data: StudentQuizAnswerRrequest) {
      try {
        return this.$store.dispatch('studentSolvedQuiz/answerQuizQuestion', {
          studioURL: this.studioURL,
          solvedQuizId: this.solvedQuiz?.id,
          data,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_ANSWER_QUIZ_QUESTION'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async retake() {
      try {
        this.inProcess = true;
        this.solvedQuiz = await this.$store.dispatch(
          'studentSolvedQuiz/retakeQuiz',
          {
            studioURL: this.studioURL,
            quizId: this.quizId,
          }
        );
        this.setCurrentScreenType('start');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: 'Failed to retake the quiz',
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async finish() {
      try {
        this.inProcess = true;
        await this.saveAnswers();
        this.solvedQuiz = await this.$store.dispatch(
          'studentSolvedQuiz/finishQuiz',
          {
            studioURL: this.studioURL,
            solvedQuizId: this.solvedQuiz?.id,
          }
        );
        this.setCurrentScreenType('finish');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async saveAnswers() {
      const selectedAnswers = this.currentQuestion.answers.filter(
        answer =>
          answer.is_selected ||
          ['text', 'upload', 'scale'].includes(answer.answer_type)
      );
      const answers = await Promise.all(
        selectedAnswers.map(answer => this.answerQuizQuestion(answer))
      );

      const firstAnswer = this.currentQuestion.answers?.[0];
      if (firstAnswer && firstAnswer.answer_type === 'upload') {
        await Promise.all(
          firstAnswer.uploads.map(file =>
            this.saveAnswerFile(answers[0].id, file.id)
          )
        );
      }

      return answers;
    },
    saveAnswerFile(solvedQuizAnswerId, fileId) {
      try {
        const { studioURL } = this;
        return this.$store.dispatch(
          'studentSolvedQuiz/addSolvedQuizAnswerFile',
          {
            studioURL,
            solvedQuizId: this.solvedQuiz?.id,
            solvedQuizAnswerId,
            fileId,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ_ANSWER_FILE'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async next() {
      try {
        this.inProcess = true;
        await this.saveAnswers();
        ++this.currentQuestionIndex;
        this.$emit('currentQuestionIndex', this.currentQuestionIndex + 1);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error.response),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    toggleAnswerIsCorrect(is_selected, answerId) {
      const multiSelect =
        this.currentQuestion.question_type === 'multiple_choice';

      this.currentQuestion.answers.forEach(answer => {
        if (answer.id === answerId) {
          answer.is_selected = is_selected;
        } else if (!multiSelect) {
          answer.is_selected = false;
        }
      });
    },
    onCheckboxGroupInputChanged(value, answer) {
      answer.answer_label = value;
    },
  },
  async mounted() {
    try {
      const studioURL = this.studioURL;
      const quizId = this.quizId;
      this.$store.dispatch('studentSolvedQuiz/viewQuiz', {
        studioURL,
        quizId,
      });
      this.quizInfo = await this.$store.dispatch('studentSolvedQuiz/getQuiz', {
        studioURL,
        quizId,
      });
      const type = this.quizInfo.is_done ? 'is_done' : 'start';
      this.setCurrentScreenType(type);
      this.$emit('quizInfo', this.quizInfo);
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
        text: errorToMessage(
          error.response,
          `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
        ),
        duration: -1,
      });
    }
  },
});
